<script lang="ts" setup>
import { AppLink } from '#components'
import { useDynamicIcons } from '~layers/shared/utils/useDynamicIcons.ts'

const { loadIcon } = useDynamicIcons()
const { loadLayout, layoutData } = useLayout()
await loadLayout()

const visibleBanners = computed(() => {
  return layoutData.value?.promoBanner?.PromoBanners?.filter((banner) => {
    let x = true
    if (banner.DateFrom && banner.DateFrom > new Date().toISOString()) {
      x = false
    }
    if (banner.DateTo && banner.DateTo < new Date().toISOString()) {
      x = false
    }
    return x
  })?.map((banner) => {
    return {
      ...banner,
    }
  }).sort((a, b) => (b.Priority || 0) - (a.Priority || 0)) || []
})

const showPromoBanners = useSessionStorage('show_promo_banners', true, { initOnMounted: true })

const interval = ref(10000)
const bannerContent = ref()
const bannerIndex = ref(0)
let i = 0

const setActiveBanner = () => {
  bannerContent.value = visibleBanners.value[i]
  bannerIndex.value = i++

  i = i > visibleBanners.value?.length - 1 ? 0 : i
}

const { isActive } = useIntervalFn(() => {
  if (isActive.value) {
    setActiveBanner()
  }
}, interval)

const countdownText = computed(() => {
  const content = bannerContent.value
  if (!content?.EnableCountdown || !content?.DateTo) return null

  const target = new Date(content.DateTo)
  if (isNaN(target.getTime())) {
    return null
  }
  const diff = target.getTime() - Date.now()
  if (diff <= 0) return null

  const minute = 60 * 1000
  const hour = 60 * minute
  const day = 24 * hour

  const totalDays = Math.floor(diff / day)
  const totalHours = Math.floor(diff / hour)
  const totalMinutes = Math.max(1, Math.floor(diff / minute))

  return { day: totalDays, hour: totalHours, minute: totalMinutes }
})

function getElement(to: any) {
  return to ? AppLink : 'div'
}

const bannerStyle = computed(() => {
  const content = bannerContent.value
  return {
    '--promo-color': content?.BackgroundColor || 'transparent',
    '--contrast-color': useContrastColor((content?.BackgroundColor || '#fff'), '#fff', '#282827'),
  }
})

onMounted(() => {
  setActiveBanner()
})
</script>

<template>
  <div
    v-if="visibleBanners?.length && showPromoBanners"
    class="promo-banner"
    :class="{ active: bannerContent }"
  >
    <Transition name="fade">
      <div
        v-if="bannerContent"
        :key="bannerIndex"
        :style="bannerStyle"
        :class="{ 'promo-banner--countdown': bannerContent.EnableCountdown }"
      >
        <div class="promo-banner__item">
          <component
            :is="getElement(bannerContent?.Icon?.Link)"
            v-if="bannerContent?.Icon?.Icon || bannerContent?.Icon?.Name"
            class="promo-item__icon"
            :to="bannerContent?.Icon?.Link"
          >
            <Icon
              v-if="bannerContent?.Icon?.Icon"
              :name="loadIcon(bannerContent.Icon.Icon)"
              class="icon"
              :class="`${bannerContent.Icon.Icon.toLowerCase()}`"
            />
            <span class="promo-item__icon-name">
              {{ bannerContent?.Icon?.Name }}
            </span>
          </component>
          <div
            v-if="bannerContent?.Text?.length"
            class="promo-item__text"
          >
            <StrapiBlocksText :nodes="bannerContent.Text" />
          </div>
          <component
            :is="getElement(bannerContent?.Icon?.Link)"
            v-if="bannerContent.EnableCountdown && countdownText"
            class="promo-item__countdown"
            :to="bannerContent?.Icon?.Link"
          >
            <i18n-t
              v-if="countdownText?.day > 0"
              class="promo-item__countdown-text day"
              keypath="| <b>{time}</b> day <i>left</i> | <b>{time}</b> days <i>left</i>"
              :params="{ time: `${countdownText.day}` }"
              :plural="countdownText.day"
              html
            />
            <i18n-t
              v-else-if="countdownText?.hour > 0"
              class="promo-item__countdown-text hour"
              keypath="| <b>{time}</b> hour <i>left</i> | <b>{time}</b> hours <i>left</i>"
              :params="{ time: `${countdownText.hour}` }"
              :plural="countdownText.hour"
              html
            />
            <i18n-t
              v-else-if="countdownText?.minute > 0"
              class="promo-item__countdown-text minute"
              keypath="| <b>{time}</b> minute <i>left</i> | <b>{time}</b> minutes <i>left</i>"
              :params="{ time: `${countdownText.minute}` }"
              :plural="countdownText.minute"
              html
            />
          </component>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style>
@import url("~layers/app/components/App/PromoBanner.css");
</style>
